import "./create-general-question.component.scss";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import tokenService from "../../services/token.service";
import { getInterviewQuestionById, postInterviewQuestion, putInterviewQuestion } from "../../services/api.service";
import config from "../../config";

type QuestionInputType = "voice" | "multiple_choice" | "free_text" | "checkbox";
type InterviewerFieldKey = "firstname" | "lastname" | "email" | "phone";
type InterviewerFieldMode = "required" | "optional";

interface ChoiceInput {
    th: string;
    en: string;
}

interface GeneralQuestionInput {
    id: string;
    th: string;
    en: string;
    type: QuestionInputType;
    choices: ChoiceInput[];
    correctIndex: number | null;
}

interface InterviewerFieldSetting {
    show: boolean;
    mode: InterviewerFieldMode;
}

const MAX_QUESTIONS = 15;
type AccessMode = "private" | "public";

const INTERVIEWER_FIELD_META: { key: InterviewerFieldKey; label: string }[] = [
    { key: "firstname", label: "ชื่อจริง" },
    { key: "lastname", label: "นามสกุล" },
    { key: "email", label: "อีเมล" },
    { key: "phone", label: "เบอร์โทรศัพท์" },
];

const DEFAULT_INTERVIEWER_FIELDS: Record<InterviewerFieldKey, InterviewerFieldSetting> = {
    firstname: { show: true, mode: "required" },
    lastname: { show: true, mode: "required" },
    email: { show: true, mode: "required" },
    phone: { show: false, mode: "optional" },
};

function modeFromCandidateRequireFlag(v: boolean | undefined): InterviewerFieldMode {
    return v ? "required" : "optional";
}

function parseInterviewerFieldsFromJobPosition(
    jp: Record<string, unknown> | null | undefined,
): Record<InterviewerFieldKey, InterviewerFieldSetting> {
    const cfg = jp?.candidate_require_config as Record<string, { show?: boolean; mode?: string }> | undefined;
    if (cfg && typeof cfg === "object") {
        const keys: InterviewerFieldKey[] = ["firstname", "lastname", "email", "phone"];
        const next: Record<InterviewerFieldKey, InterviewerFieldSetting> = { ...DEFAULT_INTERVIEWER_FIELDS };
        for (const k of keys) {
            const row = cfg[k];
            if (row && typeof row.show === "boolean" && (row.mode === "required" || row.mode === "optional")) {
                next[k] = { show: row.show, mode: row.mode };
            }
        }
        return next;
    }
    const cr = (jp?.candidate_require || {}) as Record<string, boolean | undefined>;
    return {
        firstname: { show: true, mode: modeFromCandidateRequireFlag(cr.firstname) },
        lastname: { show: true, mode: modeFromCandidateRequireFlag(cr.lastname) },
        email: { show: !!cr.email, mode: modeFromCandidateRequireFlag(cr.email) },
        phone: { show: !!cr.phone, mode: modeFromCandidateRequireFlag(cr.phone) },
    };
}

function mapApiQuestionToGeneralInput(q: Record<string, unknown>): GeneralQuestionInput {
    const id = String(q?.question_id || uuidv4());
    const th = String((q?.question as { th?: string } | undefined)?.th || "");
    const en = String((q?.question as { en?: string } | undefined)?.en || "");
    const rawChoices = Array.isArray(q?.choices) ? (q.choices as { th?: string; en?: string }[]) : [];
    const choices: ChoiceInput[] =
        rawChoices.length >= 2
            ? rawChoices.map((c) => ({ th: String(c?.th || ""), en: String(c?.en || "") }))
            : [
                  { th: "", en: "" },
                  { th: "", en: "" },
              ];

    const qt = String(q?.question_type || "");
    const at = String(q?.answer_type || "");
    const ac = q?.answer_choice as { answer_index?: number } | undefined;

    if (qt === "multiple_choice_no_answer") {
        if (at === "checkbox" || q?.ui_variant === "checkbox") {
            return { id, th, en, type: "checkbox", choices, correctIndex: null };
        }
        const idx = ac?.answer_index;
        return {
            id,
            th,
            en,
            type: "multiple_choice",
            choices,
            correctIndex:
                idx != null && Number.isInteger(Number(idx)) && Number(idx) >= 0 ? Number(idx) : null,
        };
    }

    if (qt === "multiple_choice") {
        const idx = ac?.answer_index;
        return {
            id,
            th,
            en,
            type: "multiple_choice",
            choices,
            correctIndex:
                idx != null && Number.isInteger(Number(idx)) && Number(idx) >= 0 ? Number(idx) : null,
        };
    }

    if (qt === "voice" && at === "free_text") {
        return { id, th, en, type: "free_text", choices, correctIndex: null };
    }

    return { id, th, en, type: "voice", choices, correctIndex: null };
}

const makeEmptyQuestion = (): GeneralQuestionInput => ({
    id: uuidv4(),
    th: "",
    en: "",
    type: "voice",
    choices: [
        { th: "", en: "" },
        { th: "", en: "" },
    ],
    correctIndex: null,
});

type PreservedJobPositionFields = {
    company_name: string;
    company_logo: string;
    job_title: string;
    tone: { title: string; description: string };
    interview_time: number;
    default: Record<string, unknown>;
    job_advance: Record<string, unknown>;
};

const CreateGeneralQuestionComponent = () => {
    const navigate = useNavigate();
    const { job_question_id: editJobQuestionId } = useParams<{
        company_id: string;
        job_question_id?: string;
    }>();
    const isEditMode = Boolean(editJobQuestionId);
    const [editInitialLoading, setEditInitialLoading] = useState(isEditMode);
    const [preservedJobFields, setPreservedJobFields] = useState<PreservedJobPositionFields | null>(null);

    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [objective, setObjective] = useState("");
    const [questions, setQuestions] = useState<GeneralQuestionInput[]>([makeEmptyQuestion()]);
    const [isNameModalOpen, setIsNameModalOpen] = useState(false);
    const [setName, setSetName] = useState("");
    const [accessMode, setAccessMode] = useState<AccessMode>("private");
    const [interviewerFields, setInterviewerFields] = useState<Record<InterviewerFieldKey, InterviewerFieldSetting>>(
        DEFAULT_INTERVIEWER_FIELDS,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [publicLink, setPublicLink] = useState("");
    const [isResultModalOpen, setIsResultModalOpen] = useState(false);

    const canAddMore = questions.length < MAX_QUESTIONS;
    const questionCountText = useMemo(
        () => `${questions.length}/${MAX_QUESTIONS} ข้อ`,
        [questions.length],
    );

    useEffect(() => {
        if (!isEditMode || !editJobQuestionId) {
            setEditInitialLoading(false);
            return;
        }
        let cancelled = false;
        const companyId = tokenService.getHR9CompanyUUID();
        (async () => {
            setEditInitialLoading(true);
            try {
                const res = await getInterviewQuestionById(editJobQuestionId);
                if (cancelled) return;
                if (!res?.data?.is_success || !res.data?.data) {
                    alert(String(res?.data?.error || res?.data?.message || "โหลดชุดคำถามไม่สำเร็จ"));
                    navigate(`/${companyId}/job-questions-list`);
                    return;
                }
                const d = res.data.data as Record<string, unknown>;
                const jp = d.job_position as Record<string, unknown> | undefined;
                if (String(jp?.question_type || "").toLowerCase() !== "general") {
                    alert("ชุดนี้ไม่ใช่คำถามทั่วไป — แก้ไขได้เฉพาะ General เท่านั้น");
                    navigate(`/${companyId}/job/${editJobQuestionId}`);
                    return;
                }
                setObjective(String((jp?.job_advance as { job_description?: string } | undefined)?.job_description || ""));
                setSetName(String(jp?.name || ""));
                const accRaw = String(
                    d.interview_access || jp?.interview_access || "private",
                ).toLowerCase();
                setAccessMode(accRaw === "public" ? "public" : "private");
                setInterviewerFields(parseInterviewerFieldsFromJobPosition(jp));
                const ja =
                    jp?.job_advance && typeof jp.job_advance === "object"
                        ? { ...(jp.job_advance as Record<string, unknown>) }
                        : {};
                setPreservedJobFields({
                    company_name: String(jp?.company_name || ""),
                    company_logo: String(jp?.company_logo || ""),
                    job_title: String(jp?.job_title || "General Question Set"),
                    tone:
                        jp?.tone && typeof jp.tone === "object"
                            ? (jp.tone as { title: string; description: string })
                            : { title: "General", description: "" },
                    interview_time: typeof jp?.interview_time === "number" ? jp.interview_time : 30,
                    default: jp?.default && typeof jp.default === "object" ? (jp.default as Record<string, unknown>) : {},
                    job_advance: ja,
                });
                const rawQs = d.questions;
                const mapped =
                    Array.isArray(rawQs) && rawQs.length > 0
                        ? (rawQs as Record<string, unknown>[]).map(mapApiQuestionToGeneralInput)
                        : [makeEmptyQuestion()];
                setQuestions(mapped);
            } catch (e: unknown) {
                const err = e as { response?: { data?: { error?: string } }; message?: string };
                alert(err?.response?.data?.error || err?.message || "โหลดชุดคำถามไม่สำเร็จ");
                navigate(`/${companyId}/job-questions-list`);
            } finally {
                if (!cancelled) setEditInitialLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isEditMode, editJobQuestionId, navigate]);

    const updateQuestion = (id: string, updater: (q: GeneralQuestionInput) => GeneralQuestionInput) => {
        setQuestions((prev) => prev.map((q) => (q.id === id ? updater(q) : q)));
    };

    const addQuestion = () => {
        if (!canAddMore) return;
        setQuestions((prev) => [...prev, makeEmptyQuestion()]);
    };

    const removeQuestion = (id: string) => {
        setQuestions((prev) => (prev.length <= 1 ? prev : prev.filter((q) => q.id !== id)));
    };

    const addChoice = (id: string) => {
        updateQuestion(id, (q) => ({
            ...q,
            choices: [...q.choices, { th: "", en: "" }],
        }));
    };

    const removeChoice = (id: string, choiceIndex: number) => {
        updateQuestion(id, (q) => {
            if (q.choices.length <= 2) return q;
            const nextChoices = q.choices.filter((_, idx) => idx !== choiceIndex);
            let nextCorrect = q.correctIndex;
            if (nextCorrect === choiceIndex) nextCorrect = null;
            else if (nextCorrect != null && nextCorrect > choiceIndex) nextCorrect -= 1;
            return { ...q, choices: nextChoices, correctIndex: nextCorrect };
        });
    };

    const validateStep2 = (): string | null => {
        for (let index = 0; index < questions.length; index += 1) {
            const q = questions[index];
            if (!q.th.trim() || !q.en.trim()) {
                return `กรุณากรอกคำถาม ข้อที่ ${index + 1} ให้ครบทั้งไทย/อังกฤษ`;
            }
            if (q.type === "multiple_choice" || q.type === "checkbox") {
                if (q.choices.length < 2) {
                    return `คำถามข้อที่ ${index + 1} ต้องมีตัวเลือกอย่างน้อย 2 ข้อ`;
                }
                const hasInvalidChoice = q.choices.some((c) => !c.th.trim() || !c.en.trim());
                if (hasInvalidChoice) {
                    return `กรุณากรอกตัวเลือกของคำถามข้อที่ ${index + 1} ให้ครบ`;
                }
            }
        }
        return null;
    };

    const mapQuestionToPayload = (q: GeneralQuestionInput) => {
        if (q.type === "voice") {
            return {
                question_id: q.id,
                question_type: "voice",
                answer_type: "voice",
                question: { th: q.th.trim(), en: q.en.trim() },
            };
        }
        if (q.type === "free_text") {
            return {
                question_id: q.id,
                question_type: "voice",
                answer_type: "free_text",
                question: { th: q.th.trim(), en: q.en.trim() },
            };
        }
        if (q.type === "multiple_choice") {
            const choicesPayload = q.choices.map((c) => ({ th: c.th.trim(), en: c.en.trim() }));
            const idx = q.correctIndex;
            const hasValidCorrect =
                idx != null &&
                Number.isInteger(Number(idx)) &&
                Number(idx) >= 0 &&
                Number(idx) < choicesPayload.length;
            if (hasValidCorrect) {
                return {
                    question_id: q.id,
                    question_type: "multiple_choice",
                    answer_type: "multiple_choice",
                    question: { th: q.th.trim(), en: q.en.trim() },
                    choices: choicesPayload,
                    answer_choice: {
                        answer_index: Number(idx),
                    },
                };
            }
            return {
                question_id: q.id,
                question_type: "multiple_choice_no_answer",
                answer_type: "multiple_choice_no_answer",
                question: { th: q.th.trim(), en: q.en.trim() },
                choices: choicesPayload,
            };
        }
        // checkbox: map into compatible payload (multiple_choice_no_answer)
        return {
            question_id: q.id,
            question_type: "multiple_choice_no_answer",
            answer_type: "checkbox",
            question: { th: q.th.trim(), en: q.en.trim() },
            choices: q.choices.map((c) => ({ th: c.th.trim(), en: c.en.trim() })),
            ui_variant: "checkbox",
        };
    };

    const buildPublicSetLink = (companyId: string, jobQuestionId: string) => {
        const base = String(config.share.url || "").trim().replace(/\/+$/, "");
        if (!base || !companyId || !jobQuestionId) return "";
        return `${base}/${companyId}/job-public/${jobQuestionId}`;
    };

    const submitGeneralQuestionSet = async () => {
        if (isSubmitting) return;
        if (!setName.trim()) {
            alert("กรุณาตั้งชื่อชุดคำถาม");
            return;
        }
        const profile = tokenService.getProfile();
        const companyId = tokenService.getHR9CompanyUUID();
        if (!companyId || !profile?.email) {
            alert("ไม่พบข้อมูลบริษัทหรืออีเมลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
            return;
        }

        const toRequireBool = (key: InterviewerFieldKey) =>
            interviewerFields[key].show && interviewerFields[key].mode === "required";

        const baseJob =
            isEditMode && preservedJobFields
                ? preservedJobFields
                : {
                      company_name: "",
                      company_logo: "",
                      job_title: "General Question Set",
                      tone: { title: "General", description: "" },
                      interview_time: 30,
                      default: {} as Record<string, unknown>,
                      job_advance: {} as Record<string, unknown>,
                  };

        const payload = {
            job_position: {
                company_name: baseJob.company_name,
                company_logo: baseJob.company_logo,
                job_title: baseJob.job_title,
                tone: baseJob.tone,
                interview_time: baseJob.interview_time,
                default: baseJob.default,
                name: setName.trim(),
                company_id: companyId,
                question_type: "general",
                interview_language: ["th", "en"],
                hardware_require: { is_camera: true, is_mic: true },
                job_exp: "",
                job_salary: "",
                job_level: "",
                job_employment: "",
                job_work_type: "",
                job_department: "",
                job_industry: "",
                job_advance: {
                    ...baseJob.job_advance,
                    job_description: objective.trim(),
                },
                candidate_require: {
                    email: toRequireBool("email"),
                    firstname: toRequireBool("firstname"),
                    lastname: toRequireBool("lastname"),
                    phone: toRequireBool("phone"),
                    resume: false,
                },
                candidate_require_config: interviewerFields,
                interview_access: accessMode,
            },
            email: profile.email,
            questions: questions.map(mapQuestionToPayload),
            app_id: "INTERVIEW_AI",
            template_type: "GENERAL",
            interview_access: accessMode,
        };

        try {
            setIsSubmitting(true);
            const res = isEditMode
                ? await putInterviewQuestion(editJobQuestionId!, payload)
                : await postInterviewQuestion(payload);
            if (res?.data?.is_success) {
                const savedJobQuestionId = String(
                    res?.data?.data?.job_question_id || (isEditMode ? editJobQuestionId : "") || "",
                );
                if (accessMode === "public" && savedJobQuestionId) {
                    const link = buildPublicSetLink(companyId, savedJobQuestionId);
                    setPublicLink(link);
                    setIsNameModalOpen(false);
                    setIsResultModalOpen(true);
                    setIsSubmitting(false);
                    return;
                }
                if (isEditMode && editJobQuestionId) {
                    navigate(`/${companyId}/job/${editJobQuestionId}`);
                    return;
                }
                navigate(`/${companyId}/job-questions-list`);
                return;
            }
            alert(res?.data?.error || (isEditMode ? "บันทึกการแก้ไขไม่สำเร็จ" : "สร้างคำถามทั่วไปไม่สำเร็จ"));
            setIsSubmitting(false);
        } catch (error: any) {
            alert(
                error?.response?.data?.error ||
                    (isEditMode ? "บันทึกการแก้ไขไม่สำเร็จ" : "สร้างคำถามทั่วไปไม่สำเร็จ"),
            );
            setIsSubmitting(false);
        }
    };

    if (isEditMode && editInitialLoading) {
        return (
            <div className="create-general-question-page">
                <div className="cgq-header">
                    <h1>แก้ไขคำถามทั่วไป</h1>
                    <p>กำลังโหลดข้อมูลชุดคำถาม…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="create-general-question-page">
            <div className="cgq-header">
                <h1>{isEditMode ? "แก้ไขคำถามทั่วไป" : "สร้างคำถามทั่วไป"}</h1>
                <p>Step 1: จุดประสงค์ · Step 2: ตั้งคำถาม · Step 3: ข้อมูลผู้สัมภาษณ์</p>
            </div>

            <div className="cgq-stepper border-std">
                <button className={`cgq-step ${step === 1 ? "active" : ""}`} onClick={() => setStep(1)}>
                    1. จุดประสงค์
                </button>
                <button className={`cgq-step ${step === 2 ? "active" : ""}`} onClick={() => setStep(2)}>
                    2. ตั้งคำถาม
                </button>
                <button className={`cgq-step ${step === 3 ? "active" : ""}`} onClick={() => setStep(3)}>
                    3. ข้อมูลผู้สัมภาษณ์
                </button>
            </div>

            {step === 1 && (
                <div className="cgq-card border-std">
                    <label>Objective ของชุดคำถามทั่วไป</label>
                    <textarea
                        value={objective}
                        onChange={(e) => setObjective(e.target.value)}
                        placeholder="เช่น วัดทัศนคติการทำงานร่วมทีม ความสามารถในการแก้ปัญหา และการสื่อสาร"
                    />
                    <div className="cgq-footer">
                        <button
                            className="cgq-primary"
                            onClick={() => {
                                if (!objective.trim()) {
                                    alert("กรุณากรอก objective ก่อน");
                                    return;
                                }
                                setStep(2);
                            }}
                        >
                            ถัดไป
                        </button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="cgq-card border-std">
                    <div className="cgq-card-head">
                        <strong>รายการคำถาม</strong>
                        <span>{questionCountText}</span>
                    </div>

                    {questions.map((q, qIndex) => (
                        <div className="cgq-question-block" key={q.id}>
                            <div className="cgq-question-head">
                                <strong>คำถามข้อที่ {qIndex + 1}</strong>
                                <button
                                    className="cgq-link-danger"
                                    onClick={() => removeQuestion(q.id)}
                                    disabled={questions.length <= 1}
                                >
                                    ลบ
                                </button>
                            </div>

                            <div className="cgq-grid-2">
                                <input
                                    value={q.th}
                                    onChange={(e) => updateQuestion(q.id, (x) => ({ ...x, th: e.target.value }))}
                                    placeholder="คำถาม (TH)"
                                />
                                <input
                                    value={q.en}
                                    onChange={(e) => updateQuestion(q.id, (x) => ({ ...x, en: e.target.value }))}
                                    placeholder="Question (EN)"
                                />
                            </div>

                            <div className="cgq-type-row">
                                <span>ประเภทคำตอบ</span>
                                <select
                                    value={q.type}
                                    onChange={(e) => {
                                        const nextType = e.target.value as QuestionInputType;
                                        updateQuestion(q.id, (x) => ({
                                            ...x,
                                            type: nextType,
                                            correctIndex: nextType === "multiple_choice" ? x.correctIndex : null,
                                        }));
                                    }}
                                >
                                    <option value="voice">ตอบด้วยเสียง</option>
                                    <option value="multiple_choice">Multiple choice</option>
                                    <option value="free_text">Free text</option>
                                    <option value="checkbox">Checked box</option>
                                </select>
                            </div>

                            {(q.type === "multiple_choice" || q.type === "checkbox") && (
                                <div className="cgq-choices">
                                    {q.choices.map((choice, cIndex) => (
                                        <div key={`${q.id}-${cIndex}`} className="cgq-choice-row">
                                            <input
                                                value={choice.th}
                                                onChange={(e) =>
                                                    updateQuestion(q.id, (x) => ({
                                                        ...x,
                                                        choices: x.choices.map((c, idx) =>
                                                            idx === cIndex ? { ...c, th: e.target.value } : c,
                                                        ),
                                                    }))
                                                }
                                                placeholder={`ตัวเลือก ${cIndex + 1} (TH)`}
                                            />
                                            <input
                                                value={choice.en}
                                                onChange={(e) =>
                                                    updateQuestion(q.id, (x) => ({
                                                        ...x,
                                                        choices: x.choices.map((c, idx) =>
                                                            idx === cIndex ? { ...c, en: e.target.value } : c,
                                                        ),
                                                    }))
                                                }
                                                placeholder={`Choice ${cIndex + 1} (EN)`}
                                            />
                                            <button
                                                className="cgq-icon-btn"
                                                onClick={() => removeChoice(q.id, cIndex)}
                                                disabled={q.choices.length <= 2}
                                            >
                                                ลบ
                                            </button>
                                        </div>
                                    ))}
                                    <div className="cgq-choice-actions">
                                        <button className="cgq-link" onClick={() => addChoice(q.id)}>
                                            + เพิ่มตัวเลือก
                                        </button>
                                    </div>
                                    {q.type === "multiple_choice" && (
                                        <div className="cgq-correct-row">
                                            <span>คำตอบที่ถูก (ไม่บังคับ)</span>
                                            <select
                                                value={q.correctIndex ?? ""}
                                                onChange={(e) =>
                                                    updateQuestion(q.id, (x) => ({
                                                        ...x,
                                                        correctIndex: e.target.value === "" ? null : Number(e.target.value),
                                                    }))
                                                }
                                            >
                                                <option value="">ไม่กำหนด — แบบแบบสำรวจ</option>
                                                {q.choices.map((_, idx) => (
                                                    <option key={idx} value={idx}>
                                                        ตัวเลือก {idx + 1}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    <div className="cgq-actions-row">
                        <button className="cgq-secondary" onClick={addQuestion} disabled={!canAddMore}>
                            + เพิ่มคำถาม
                        </button>
                        <button className="cgq-secondary" onClick={() => setStep(1)}>
                            ย้อนกลับ
                        </button>
                        <button
                            className="cgq-primary"
                            onClick={() => {
                                const err = validateStep2();
                                if (err) {
                                    alert(err);
                                    return;
                                }
                                setStep(3);
                            }}
                        >
                            ถัดไป
                        </button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="cgq-card border-std">
                    <div className="cgq-card-head">
                        <strong>ตั้งค่าข้อมูลผู้สัมภาษณ์</strong>
                        <span>เลือก Show/Hide และ Require/Optional</span>
                    </div>

                    <div className="cgq-interviewer-table">
                        <div className="cgq-interviewer-head">ฟิลด์ข้อมูล</div>
                        <div className="cgq-interviewer-head">การแสดงผล</div>
                        <div className="cgq-interviewer-head">เงื่อนไขการกรอก</div>

                        {INTERVIEWER_FIELD_META.map((item) => {
                            const setting = interviewerFields[item.key];
                            return (
                                <div className="cgq-interviewer-row" key={item.key}>
                                    <div className="cgq-interviewer-label">{item.label}</div>
                                    <select
                                        value={setting.show ? "show" : "hide"}
                                        onChange={(e) => {
                                            const nextShow = e.target.value === "show";
                                            setInterviewerFields((prev) => ({
                                                ...prev,
                                                [item.key]: {
                                                    ...prev[item.key],
                                                    show: nextShow,
                                                    mode: nextShow ? prev[item.key].mode : "optional",
                                                },
                                            }));
                                        }}
                                    >
                                        <option value="show">Show</option>
                                        <option value="hide">Hide</option>
                                    </select>
                                    <select
                                        value={setting.mode}
                                        disabled={!setting.show}
                                        onChange={(e) => {
                                            const nextMode = e.target.value as InterviewerFieldMode;
                                            setInterviewerFields((prev) => ({
                                                ...prev,
                                                [item.key]: { ...prev[item.key], mode: nextMode },
                                            }));
                                        }}
                                    >
                                        <option value="required">Require</option>
                                        <option value="optional">Optional</option>
                                    </select>
                                </div>
                            );
                        })}
                    </div>

                    <div className="cgq-actions-row">
                        <button className="cgq-secondary" onClick={() => setStep(2)}>
                            ย้อนกลับ
                        </button>
                        <button className="cgq-primary" onClick={() => setIsNameModalOpen(true)}>
                            ตั้งชื่อและบันทึก
                        </button>
                    </div>
                </div>
            )}

            {isNameModalOpen && (
                <div className="cgq-modal-overlay" onClick={() => setIsNameModalOpen(false)}>
                    <div className="cgq-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cgq-modal-header">
                            <h3>{isEditMode ? "ตรวจทานและบันทึก" : "ตั้งชื่อชุดคำถาม"}</h3>
                            <p>
                                {isEditMode
                                    ? "แก้ชื่อชุด ประเภทการเข้าถึง แล้วบันทึก (ยังไม่มีผู้สมัครเท่านั้น)"
                                    : "ตั้งชื่อและเลือกประเภทการเข้าถึงก่อนเผยแพร่"}
                            </p>
                        </div>
                        <div className="cgq-modal-field">
                            <label className="cgq-field-label">ชื่อชุดคำถาม</label>
                            <input
                                value={setName}
                                onChange={(e) => setSetName(e.target.value)}
                                placeholder="เช่น General - Leadership Screening"
                            />
                        </div>
                        <div className="cgq-modal-field">
                            <label className="cgq-field-label">ประเภทการเข้าถึง</label>
                            <div className="cgq-access-cards">
                                <div
                                    className={`cgq-access-card ${accessMode === "private" ? "selected" : ""}`}
                                    onClick={() => setAccessMode("private")}
                                >
                                    <div className="cgq-access-card-icon">🔒</div>
                                    <div className="cgq-access-card-body">
                                        <strong>Private</strong>
                                        <span>เชิญผู้สมัครผ่านอีเมลก่อนเข้าทำ</span>
                                    </div>
                                    <div className="cgq-access-card-check">
                                        {accessMode === "private" && <span className="cgq-check-dot" />}
                                    </div>
                                </div>
                                <div
                                    className={`cgq-access-card ${accessMode === "public" ? "selected" : ""}`}
                                    onClick={() => setAccessMode("public")}
                                >
                                    <div className="cgq-access-card-icon">🔗</div>
                                    <div className="cgq-access-card-body">
                                        <strong>Public</strong>
                                        <span>สร้างลิงก์สาธารณะ เข้าทำได้ทันที</span>
                                    </div>
                                    <div className="cgq-access-card-check">
                                        {accessMode === "public" && <span className="cgq-check-dot" />}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="cgq-modal-actions">
                            <button className="cgq-secondary" onClick={() => setIsNameModalOpen(false)}>
                                ยกเลิก
                            </button>
                            <button
                                className="cgq-primary"
                                onClick={submitGeneralQuestionSet}
                                disabled={isSubmitting}
                            >
                                {isSubmitting
                                    ? "กำลังบันทึก..."
                                    : isEditMode
                                      ? "บันทึกการแก้ไข"
                                      : "บันทึกชุดคำถาม"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isResultModalOpen && (
                <div className="cgq-modal-overlay" onClick={() => setIsResultModalOpen(false)}>
                    <div className="cgq-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{isEditMode ? "บันทึกการแก้ไขสำเร็จ" : "สร้างชุดคำถามสำเร็จ"}</h3>
                        {publicLink ? (
                            <>
                                <p className="cgq-help-text">Public link สำหรับผู้เข้าสัมภาษณ์:</p>
                                <input value={publicLink} readOnly />
                                <div className="cgq-modal-actions">
                                    <button
                                        className="cgq-secondary"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(publicLink);
                                                alert("คัดลอกลิงก์แล้ว");
                                            } catch {
                                                alert("คัดลอกลิงก์ไม่สำเร็จ");
                                            }
                                        }}
                                    >
                                        คัดลอกลิงก์
                                    </button>
                                    <button
                                        className="cgq-primary"
                                        onClick={() => {
                                            const cid = tokenService.getHR9CompanyUUID();
                                            if (isEditMode && editJobQuestionId) {
                                                navigate(`/${cid}/job/${editJobQuestionId}`);
                                            } else {
                                                navigate(`/${cid}/job-questions-list`);
                                            }
                                        }}
                                    >
                                        {isEditMode ? "ไปหน้าชุดคำถาม" : "กลับไปหน้ารายการ"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="cgq-modal-actions">
                                <button
                                    className="cgq-primary"
                                    onClick={() => {
                                        const cid = tokenService.getHR9CompanyUUID();
                                        if (isEditMode && editJobQuestionId) {
                                            navigate(`/${cid}/job/${editJobQuestionId}`);
                                        } else {
                                            navigate(`/${cid}/job-questions-list`);
                                        }
                                    }}
                                >
                                    {isEditMode ? "ไปหน้าชุดคำถาม" : "กลับไปหน้ารายการ"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateGeneralQuestionComponent;
